import { safeRedirect } from '@diario/common';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clasesDeBotonCuadrado } from '../../../components/ui/botonCuadrado';
import { Button } from '../../../components/ui/button';
import { useSesion } from '../api/useSesion';

export interface AvisoModoVisitanteProps {
	/** Clase adicional para el contenedor de la pastilla (posicionamiento aparte: eso ya lo fija
	 * el propio componente, siempre en flujo, para no tapar la acción principal ni el contenido —
	 * ver H-33/T-31). */
	className?: string;
	/** Ruta a conservar como destino de retorno tras el acceso. Por defecto, la ruta actual del
	 * navegador (`window.location`, válida indistintamente de cómo navegue el router — la API
	 * de historial es la misma) — se puede forzar desde fuera si quien monta el componente ya
	 * tiene un valor mejor a mano. */
	destino?: string;
}

const destinoDelNavegador = (): string =>
	typeof window === 'undefined' ? '/' : `${window.location.pathname}${window.location.search}`;

/** Pastilla (historial: H-06 → T-6 → H-26 → T-26 → H-33 → T-31). Vive **siempre en el flujo**
 * del documento, en todos los anchos — `static`, sin ningún `sm:sticky`/`sm:bottom-*` — porque
 * cualquier variante pegada a un borde (fija o `sticky`) cubre lo que quede en esa franja del
 * primer pantallazo por construcción: T-26 ya lo había resuelto así por debajo de `sm` (H-26,
 * tapaba «Entrar» y «¿Olvidaste tu contraseña?» en móvil); H-33 midió que la franja inferior que
 * se conservaba desde `sm` tapa el mismo botón, el mismo enlace o el campo de contraseña en
 * `/login` con ventanas de escritorio de poca altura (660-860 px a 1280, y a 1440), así que deja
 * de tener sentido mantener dos comportamientos: uno solo, en flujo, cierra ambos hallazgos con
 * el mismo mecanismo. Radio de panel (`rounded-lg`, no `rounded-pill`) y el mismo papel
 * translúcido y sombra de flotación del sistema (`bg-paper/85 shadow-float`) en todos los anchos;
 * sin `backdrop-blur` — desenfocaba lo que hubiera *detrás* de la pastilla cuando flotaba sobre
 * el contenido, y en flujo no hay nada detrás salvo el fondo plano de la página, así que no
 * aporta nada visible (T-31, sin verificación A/B pixel a pixel, por criterio: no hay superficie
 * bajo la que desenfocar). Desde `sm` mantiene la fila única (`sm:flex-row sm:items-center
 * sm:justify-between`, en el propio JSX, no aquí) para no crecer de ~68 a ~100 px de alto. Desde
 * T-2.1 esta constante ya solo viste la rama expandida: el minimizado es solo el botón para volver
 * a expandir, sin papel (`bg-paper/85`) ni sombra de flotación (`shadow-float`) propios, porque sin
 * la franja del mensaje no queda contenido que necesite una superficie que lo separe del fondo — esa
 * caja alrededor de un único botón se leía como una barra ajena a todo el ancho, no como parte del
 * control (T-2.1). `__root.tsx` monta una única instancia de `AvisoModoVisitante`, siempre en el
 * mismo hueco, antes de `<main>`. Un envoltorio (`AvisoModoVisitante`)
 * alinea sus bordes izquierdo y derecho con la caja de contenido de `<main>`
 * (`mx-auto max-w-3xl px-4 sm:px-6`, igual que `<main>`), así la pastilla nunca desborda ni queda
 * más estrecha que el contenido que decora. El requisito 2 de CA-02b.11 («anclado abajo, franja
 * inferior delgada») deja de cumplirse a propósito, en todos los anchos, no solo en móvil: es la
 * pregunta 6 de «Preguntas abiertas para el cliente» en `plan-mejora.md`. */
const CLASE_PASTILLA = 'static my-2 rounded-lg bg-paper/85 shadow-float';

/**
 * Aviso de modo visitante (documento 02b, criterios CA-02b.11). Cinco requisitos del cliente:
 * 1. Visible en toda ruta sin protección mientras no haya sesión — por eso consulta `useSesion`
 *    él mismo y no se muestra hasta que `GET /me` confirma `ERR-006`; así la ruta que lo monta
 *    (oleada 3) no tiene que repetir esa lógica, solo montarlo.
 * 2. El cliente pide una franja inferior anclada que no tape ni la acción principal ni el
 *    contenido; en la práctica cualquier franja pegada a un borde cubre lo que quede en esa
 *    banda del primer pantallazo (H-26 en móvil, H-33 en escritorio con poca altura de
 *    viewport), así que desde T-31 el aviso vive **siempre en el flujo**, entre la cabecera y
 *    `<main>`, en todos los anchos: desplaza el contenido en vez de superponerse, y por
 *    construcción no puede tapar nada. `__root.tsx` monta una sola instancia, siempre en el
 *    mismo hueco, así que nunca hay dos avisos en el árbol de accesibilidad.
 * 3. El texto cierra con «protegemos a la comunidad» — ya está en `avisoVisitante.mensaje`, no
 *    se reescribe aquí.
 * 4. Lleva al acceso conservando el destino de retorno (`safeRedirect` antes de construir el
 *    enlace).
 * 5. Se puede minimizar, nunca cerrar del todo: en minimizado sigue presente, en forma reducida.
 */
export const AvisoModoVisitante = ({ className = '', destino }: AvisoModoVisitanteProps): React.JSX.Element | null => {
	const { t } = useTranslation('auth');
	const { noHaySesion } = useSesion();
	const [minimizado, setMinimizado] = useState(false);

	// Requisito 1: nada que mostrar hasta que se confirme que no hay sesión (ni durante la
	// carga ni cuando `noSeConfirmo` — un fallo de red no es un permiso para asumir "visitante").
	if (!noHaySesion) {
		return null;
	}

	// Navegación en cliente (el equivalente exacto de `urlAcceso` de antes, pero por `Link` del
	// router en vez de `<a href>` crudo, para no recargar el documento — ver `Cabecera`, molde de
	// lectura, con el mismo `to='/login'` sin recarga): `search` deja que el propio router
	// serialice `?redirect=…`, con el mismo destino ya saneado por `safeRedirect`.
	const destinoSeguro = safeRedirect(destino ?? destinoDelNavegador());

	// H-33/T-31: el mismo ancho de caja de contenido que `<main>` (`routes/__root.tsx`), para que
	// los bordes izquierdo y derecho de la pastilla coincidan con los del contenido en cualquier
	// ancho — la pastilla es un `<div>` de bloque sin ancho propio, así que hereda el del
	// envoltorio.
	return (
		<div className='mx-auto max-w-3xl px-4 sm:px-6'>
			{minimizado ? (
				<div className={`static my-2 flex justify-end ${className}`.trim()}>
					<Button onClick={() => setMinimizado(false)} size='sm' type='button' variant='secondary'>
						{t('avisoVisitante.expandir')}
					</Button>
				</div>
			) : (
				<div
					className={`${CLASE_PASTILLA} flex flex-col items-start gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${className}`}
					role='status'
				>
					<p className='text-base text-ink'>{t('avisoVisitante.mensaje')}</p>
					<div className='flex shrink-0 gap-2'>
						<Link className={clasesDeBotonCuadrado({ size: 'sm' })} search={{ redirect: destinoSeguro }} to='/login'>
							{t('avisoVisitante.accion')}
						</Link>
						<Button onClick={() => setMinimizado(true)} type='button' variant='secondary'>
							{t('avisoVisitante.minimizar')}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};
